/**
 * useFutureBotAgent — client side of the FutureBot agent loop.
 *
 * Owns the chat state and orchestrates turns against the futureBotAgent
 * Cloud Function (us-east1):
 *
 *   send(text)
 *     -> callable({ messages, pageId, pageContext, sessionId })
 *     -> { type: 'text' }        : show the reply, done.
 *     -> { type: 'tool_calls' }  : execute each call against the tools the
 *        current page registered (the user literally watches nodes appear),
 *        append functionResponses to the transcript, call again. Repeat until
 *        the model answers with text or the tool budget below runs out.
 *
 * Tool budget (per user message): the model gets MAX_TOOL_ROUNDS rounds /
 * MAX_TOOL_CALLS total calls. Approaching the cap, a wrap-up notice is
 * attached to the last tool result; once the cap is hit the final backend
 * call goes out with toolMode 'none' (function calling disabled) so the model
 * must answer with what it has instead of looping — e.g., re-filtering per
 * user to enumerate counts one at a time.
 *
 * Two message representations:
 *   - display messages (user / ai / activity / error) for the UI, persisted
 *     to localStorage and mirrored to Firestore users/{uid}/aichatlog so the
 *     existing chatbot analytics keep working.
 *   - the Gemini Content[] transcript (kept in a ref; rebuilt text-only from
 *     persisted history after a reload).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useFutureBot } from './FutureBotProvider';
import { useQuantumMatrixTools } from './useQuantumMatrixTools';

const fbFunctions = getFunctions(undefined, 'us-east1');

const MESSAGES_STORAGE_KEY = 'futureBotMessages';
const SESSION_STORAGE_KEY = 'futureBotSessionId';
const MAX_TOOL_ROUNDS = 8; // tool_calls responses per user message
const MAX_TOOL_CALLS = 20; // total tool executions per user message
const WRAP_UP_ROUNDS = 6; // warn the model from this round on...
const WRAP_UP_CALLS = 14; // ...or once this many calls have run
const TOOL_STAGGER_MS = 400;
const MAX_PERSISTED_MESSAGES = 200;

const generateId = () => crypto.randomUUID();

// ---------------------------------------------------------------------------
// Tool activity labels
// ---------------------------------------------------------------------------

function prettySubType(subType) {
  if (!subType) return 'step';
  return subType
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

export function describeToolCall(name, args = {}) {
  switch (name) {
    case 'get_campaign_state':
      return 'Reading the canvas';
    case 'build_campaign': {
      const count = Array.isArray(args.nodes) ? args.nodes.length : '?';
      const title = args.template?.name ? `"${args.template.name}"` : 'campaign';
      return `Building ${title} — ${count} steps`;
    }
    case 'add_node':
      return `Adding step — ${prettySubType(args.subType)}`;
    case 'update_node_config':
      return 'Updating step';
    case 'change_node_type':
      return `Changing step to ${prettySubType(args.subType)}`;
    case 'remove_node':
      return 'Removing step';
    case 'move_node':
      return 'Reordering steps';
    case 'set_false_branch':
      return 'Rewiring branch';
    case 'set_trigger':
      return `Setting trigger — ${prettySubType(args.triggerType)}`;
    case 'set_campaign_meta':
      return 'Updating campaign details';
    case 'save_campaign':
      return 'Saving campaign';
    // Table pages (dashboard contacts / my deals)
    case 'get_view_state':
      return 'Reading the table view';
    case 'set_filters': {
      const count = Array.isArray(args.filters) ? args.filters.length : 0;
      return count === 0 ? 'Clearing filters' : `Applying ${count} filter${count === 1 ? '' : 's'}`;
    }
    case 'clear_filters':
      return 'Clearing filters';
    case 'set_search':
      return args.searchTerm ? `Searching "${args.searchTerm}"` : 'Clearing search';
    case 'set_sort':
      return `Sorting by ${args.field || '?'} (${args.direction || 'desc'})`;
    case 'set_owner_scope':
      return args.userId ? 'Switching owner scope' : 'Showing all users';
    case 'list_smart_lists':
      return 'Checking smartlists';
    case 'apply_smart_list':
      return args.name ? `Applying smartlist "${args.name}"` : 'Applying smartlist';
    case 'create_smart_list':
      return args.name ? `Saving smartlist "${args.name}"` : 'Saving smartlist';
    case 'update_smart_list':
      return 'Updating smartlist';
    case 'delete_smart_list':
      return 'Deleting smartlist';
    // Analytics page
    case 'get_analytics_state':
      return 'Reading analytics state';
    case 'set_analytics_filters':
      return 'Updating analytics filters';
    case 'set_active_tab': {
      const tabs = ['Overview', 'User Activity', 'Task Health', 'Lead Health', 'Monday Report', 'Campaigns'];
      const label = tabs[args.tabIndex];
      return label ? `Opening the ${label} tab` : 'Switching tab';
    }
    case 'get_analytics_data':
      return 'Analyzing on-screen data';
    case 'run_analytics_query':
      return 'Running comparison query';
    // Mortgage Monitor
    case 'get_monitor_summary':
      return 'Reading the Mortgage Monitor';
    case 'list_refi_opportunities':
      return args.status ? `Listing ${prettySubType(args.status).toLowerCase()} opportunities` : 'Listing refi opportunities';
    case 'set_monitor_filters':
      return 'Filtering the opportunity table';
    case 'clear_monitor_filters':
      return 'Clearing table filters';
    case 'enroll_refi_opportunity':
      return 'Enrolling borrower in refi nurture';
    case 'dismiss_refi_opportunity':
      return 'Dismissing opportunity';
    // Leaderboard
    case 'get_leaderboard':
      return args.period ? `Reading the ${args.period.replace('_', ' ')} leaderboard` : 'Reading the leaderboard';
    case 'get_presidents_club_status':
      return "Checking President's Club progress";
    case 'get_my_coaching_review':
      return args.generate ? 'Generating your coaching review' : 'Reading your coaching review';
    // Dashboard daily briefing
    case 'get_daily_briefing':
      return args.regenerate ? 'Rebuilding your briefing' : 'Reading your daily briefing';
    // Signature Studio
    case 'get_signature_state':
      return 'Reading your signature';
    case 'apply_template':
      return args.templateId ? `Applying the ${args.templateId.replace(/-/g, ' ')} template` : 'Applying template';
    case 'import_current_signature':
      return 'Importing your Gmail signature';
    case 'replace_signature':
      return 'Designing your signature';
    case 'update_global_style':
      return 'Updating signature styles';
    case 'rename_signature':
      return args.name ? `Renaming to "${args.name}"` : 'Renaming signature';
    case 'add_row':
      return 'Adding a row';
    case 'remove_row':
      return 'Removing a row';
    case 'update_column':
      return 'Adjusting column layout';
    case 'add_block':
      return args.block?.type ? `Adding ${prettySubType(args.block.type).toLowerCase()} block` : 'Adding a block';
    case 'update_block':
      return 'Editing a block';
    case 'remove_block':
      return 'Removing a block';
    case 'move_block':
      return 'Moving a block';
    case 'list_images':
      return 'Checking your image library';
    case 'undo_last_change':
      return 'Undoing the last change';
    // Quantum Matrix (global lender-guideline tools)
    case 'qm_match':
      return 'Matching lenders for this scenario';
    case 'qm_ask':
      return 'Searching lender guidelines (can take a minute or two)';
    case 'qm_advise':
      return 'Consulting the guideline advisor';
    default:
      return prettySubType(name);
  }
}

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

function loadPersistedMessages() {
  try {
    const raw = localStorage.getItem(MESSAGES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Gemini fileData parts for a display message's attachments (gs:// URIs). */
function attachmentParts(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments
    .filter((a) => a && a.gsUri && a.mimeType)
    .map((a) => ({ fileData: { fileUri: a.gsUri, mimeType: a.mimeType } }));
}

/**
 * Rebuild a Gemini transcript from persisted display messages: text turns
 * plus attachment fileData parts (tool exchanges are not persisted).
 */
function rebuildTranscript(displayMessages) {
  const contents = [];
  for (const msg of displayMessages) {
    if (msg.type === 'user') {
      const parts = [...attachmentParts(msg.attachments), ...(msg.text ? [{ text: msg.text }] : [])];
      if (parts.length > 0) contents.push({ role: 'user', parts });
    } else if (msg.type === 'ai' && msg.text) {
      contents.push({ role: 'model', parts: [{ text: msg.text }] });
    }
  }
  return contents;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFutureBotAgent() {
  const { currentUser } = useAuth();
  const { currentPage } = useFutureBot();

  const [messages, setMessages] = useState(loadPersistedMessages);
  const [isResponding, setIsResponding] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(SESSION_STORAGE_KEY) || generateId()
  );

  const transcriptRef = useRef(null);
  if (transcriptRef.current === null) {
    transcriptRef.current = rebuildTranscript(loadPersistedMessages());
  }
  const pageRef = useRef(currentPage);
  pageRef.current = currentPage;
  const cancelRef = useRef(false);

  // Global Quantum Matrix tools: available on every page, with per-session
  // multi-turn state managed inside the hook (persisted to localStorage).
  const { tools: qmTools, resetSession: resetQmSession } = useQuantumMatrixTools({
    sessionId,
    cancelRef,
  });
  const qmToolsRef = useRef(qmTools);
  qmToolsRef.current = qmTools;

  useEffect(() => {
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }, [sessionId]);

  useEffect(() => {
    try {
      localStorage.setItem(
        MESSAGES_STORAGE_KEY,
        JSON.stringify(messages.slice(-MAX_PERSISTED_MESSAGES))
      );
    } catch {
      /* quota errors are non-fatal */
    }
  }, [messages]);

  // --- display message helpers ---------------------------------------------

  const appendMessage = useCallback((msg) => {
    const id = generateId();
    setMessages((prev) => [...prev, { id, createdAt: Date.now(), ...msg }]);
    return id;
  }, []);

  const patchMessage = useCallback((id, updates) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  }, []);

  // --- Firestore mirror (keeps /admin/chatbot-analytics working) -----------

  const logToFirestore = useCallback(
    async (type, text) => {
      if (!currentUser?.uid || !text) return;
      try {
        await addDoc(collection(db, 'users', currentUser.uid, 'aichatlog'), {
          user:
            type === 'user'
              ? {
                  displayName: currentUser.displayName || 'You',
                  photoURL: currentUser.photoURL || null,
                  uid: currentUser.uid,
                }
              : { displayName: 'FutureBot', photoURL: null, uid: 'ai-assistant' },
          message: text,
          sessionId,
          timestamp: serverTimestamp(),
          type,
        });
      } catch (err) {
        console.warn('[FutureBot] Failed to log message:', err.message);
      }
    },
    [currentUser, sessionId]
  );

  // --- tool execution -------------------------------------------------------

  const executeToolCall = useCallback(async (call) => {
    // Page-registered tools win; the global Quantum Matrix tools are the
    // fallback available everywhere.
    const tools = pageRef.current?.tools;
    const impl = tools?.[call.name] || qmToolsRef.current?.[call.name];
    if (!impl) {
      return {
        error: `Tool "${call.name}" is not available on the user's current page (${pageRef.current?.title || 'unknown'}). Do not retry it here.`,
      };
    }
    try {
      const result = await impl(call.args || {});
      if (result === undefined || result === null) return { ok: true };
      if (typeof result !== 'object' || Array.isArray(result)) return { result };
      return result;
    } catch (err) {
      console.error(`[FutureBot] Tool ${call.name} threw:`, err);
      return { error: err.message || 'Tool execution failed' };
    }
  }, []);

  const buildPageContext = useCallback(async () => {
    const page = pageRef.current;
    const context = { route: page.route, title: page.title };
    if (page.getContext) {
      try {
        const extra = await page.getContext();
        if (extra?.referenceData) context.referenceData = extra.referenceData;
        if (extra?.stateSummary) context.stateSummary = extra.stateSummary;
      } catch (err) {
        console.warn('[FutureBot] getContext failed:', err.message);
      }
    }
    return context;
  }, []);

  // --- the agent loop --------------------------------------------------------

  const send = useCallback(
    async (rawText, rawAttachments = []) => {
      const text = (rawText || '').trim();
      const attachments = (Array.isArray(rawAttachments) ? rawAttachments : [])
        .filter((a) => a && a.gsUri && a.mimeType)
        .map((a) => ({
          name: a.name || 'file',
          size: a.size || 0,
          mimeType: a.mimeType,
          storagePath: a.storagePath || null,
          gsUri: a.gsUri,
          downloadURL: a.downloadURL || null,
        }));
      if ((!text && attachments.length === 0) || isResponding || !currentUser?.uid) return;

      cancelRef.current = false;
      appendMessage({ type: 'user', text, ...(attachments.length ? { attachments } : {}) });
      logToFirestore(
        'user',
        attachments.length
          ? `[Attached: ${attachments.map((a) => a.name).join(', ')}]${text ? ` ${text}` : ''}`
          : text
      );
      setIsResponding(true);

      const userParts = [...attachmentParts(attachments), ...(text ? [{ text }] : [])];
      let contents = [...transcriptRef.current, { role: 'user', parts: userParts }];
      const callAgent = httpsCallable(fbFunctions, 'futureBotAgent', { timeout: 120000 });

      let toolRounds = 0;
      let toolCallsExecuted = 0;

      try {
        // Bounded by the tool budget plus one final forced-text turn.
        for (let turn = 0; turn <= MAX_TOOL_ROUNDS; turn++) {
          if (cancelRef.current) break;

          const budgetExhausted =
            toolRounds >= MAX_TOOL_ROUNDS || toolCallsExecuted >= MAX_TOOL_CALLS;

          const pageContext = await buildPageContext();
          const { data } = await callAgent({
            messages: contents,
            pageId: pageRef.current.pageId,
            pageContext,
            sessionId,
            ...(budgetExhausted ? { toolMode: 'none' } : {}),
          });

          if (data.modelContent) {
            contents = [...contents, data.modelContent];
          }

          if (data.type === 'text') {
            appendMessage({ type: 'ai', text: data.text });
            logToFirestore('ai', data.text);
            break;
          }

          if (data.type === 'tool_calls' && Array.isArray(data.calls) && data.calls.length > 0) {
            if (budgetExhausted) {
              // Backend forces text past the budget, so this only happens on
              // a stale deploy. Drop the unanswered tool-call turn so the
              // transcript stays valid, and stop.
              contents = contents.slice(0, -1);
              const notice =
                'I hit the tool-use limit for this request, so I stopped here. Ask a follow-up to keep going.';
              appendMessage({ type: 'error', text: notice });
              logToFirestore('error', notice);
              break;
            }
            toolRounds += 1;

            if (data.text) {
              appendMessage({ type: 'ai', text: data.text });
            }

            const responseParts = [];
            for (const call of data.calls) {
              // Every issued call gets a response — a partially answered
              // round would poison the transcript for the next message.
              if (cancelRef.current) {
                responseParts.push({
                  functionResponse: {
                    ...(call.id ? { id: call.id } : {}),
                    name: call.name,
                    response: { error: 'Cancelled by the user before this call ran.' },
                  },
                });
                continue;
              }
              const activityId = appendMessage({
                type: 'activity',
                text: describeToolCall(call.name, call.args),
                status: 'running',
              });
              // eslint-disable-next-line no-await-in-loop
              const result = await executeToolCall(call);
              toolCallsExecuted += 1;
              patchMessage(activityId, { status: result.error ? 'error' : 'done' });
              responseParts.push({
                functionResponse: {
                  ...(call.id ? { id: call.id } : {}),
                  name: call.name,
                  response: result,
                },
              });
              // eslint-disable-next-line no-await-in-loop
              await new Promise((r) => setTimeout(r, TOOL_STAGGER_MS));
            }

            // Nudge the model to wrap up before the hard cap cuts it off.
            const nearBudget =
              toolRounds >= WRAP_UP_ROUNDS || toolCallsExecuted >= WRAP_UP_CALLS;
            if (nearBudget && responseParts.length > 0) {
              const last = responseParts[responseParts.length - 1];
              last.functionResponse.response = {
                ...last.functionResponse.response,
                _budgetNotice:
                  `Tool budget nearly exhausted (round ${toolRounds}/${MAX_TOOL_ROUNDS}, ` +
                  `${toolCallsExecuted}/${MAX_TOOL_CALLS} calls this request). Stop calling tools and ` +
                  'answer the user now with what you already have. Do not iterate through more filter values.',
              };
            }

            contents = [...contents, { role: 'user', parts: responseParts }];
            continue;
          }

          // Unknown response shape — stop cleanly.
          break;
        }
      } catch (err) {
        console.error('[FutureBot] Agent turn failed:', err);
        const friendly = err.message?.includes('deadline')
          ? 'That took too long and timed out. Try again or break the request into smaller steps.'
          : `Something went wrong: ${err.message || 'unknown error'}`;
        appendMessage({ type: 'error', text: friendly });
        logToFirestore('error', friendly);
      } finally {
        transcriptRef.current = contents;
        setIsResponding(false);
      }
    },
    [
      isResponding,
      currentUser?.uid,
      sessionId,
      appendMessage,
      patchMessage,
      logToFirestore,
      executeToolCall,
      buildPageContext,
    ]
  );

  const stop = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const startNewChat = useCallback(() => {
    cancelRef.current = true;
    transcriptRef.current = [];
    resetQmSession();
    setMessages([]);
    setSessionId(generateId());
    setIsResponding(false);
  }, [resetQmSession]);

  return {
    messages,
    isResponding,
    sessionId,
    currentPage,
    send,
    stop,
    startNewChat,
  };
}

export default useFutureBotAgent;
